import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {OrientationEnum} from 'common/proxies/enum-proxy';
import styles from './index.css';
import {
  useChartId,
  useChartInteraction,
  useChartLegend,
  useDynamicChartWidth,
  useWindowResize,
} from './hooks.js';

import InteractionSurface from 'components/interaction-surface';
import Legend from 'components/legend';
import StripeDefs from 'components/stripe-defs';
import Tooltip from 'components/tooltip';

const useConfig = (name, ...configs) => {
  for (const config of configs) {
    if (Number(config[name]) > -1) {
      return config[name];
    }
  }
  return 0;
};

const ScrollableChart = ({
  children,
  className,
  configuration,
  containerHeight,
  height,
  onInteraction,
  setWidth,
  showLegend,
  showTooltip,
}) => {
  useWindowResize();

  const chartRef = useRef();
  const containerRef = useRef();

  const chartWidth = useDynamicChartWidth(chartRef, setWidth);
  const chartId = useChartId();

  const [interaction, events, interactionConfig] = useChartInteraction(
    {
      height,
      ...configuration.interaction,
    },
    containerRef,
    onInteraction,
  );

  const [legendHeight, setLegendRows] = useChartLegend(10);
  const legendSeries = configuration.legend.series || [];

  const tooltipConfig = {
    series: [],
    ...configuration.tooltip,
  };
  return (
    <div className={styles.ScrollableChart}>
      {showLegend ? (
        <svg
          className={classNames(
            `${className}--Legend`,
            styles.ScrollableChartLegend,
          )}
          height={legendHeight + 20}
          width={chartWidth}>
          <Legend
            height={legendHeight}
            series={legendSeries}
            setLegendRows={setLegendRows}
            width={chartWidth}
          />
        </svg>
      ) : null}
      <div style={{position: 'relative'}}>
        <div
          className={styles.ScrollableChartContainer}
          ref={containerRef}
          style={{
            height: containerHeight ? containerHeight : height,
            overflowY: containerHeight ? 'scroll' : 'visible',
          }}>
          <svg
            className={classNames(className, styles.ScrollableChartContent)}
            height={height}
            ref={chartRef}
            width={chartWidth}>
            <StripeDefs id={chartId} />
            {children}
            <InteractionSurface
              direction={interactionConfig.direction}
              handleMove={events.move}
              handleOut={events.leave}
              height={interactionConfig.height}
              tickRange={interactionConfig.tickRange}
              width={chartWidth}
              x={interactionConfig.x}
              y={interactionConfig.y}
            />
          </svg>
        </div>
        {showTooltip ? (
          <svg
            className={classNames(
              `${className}--Tooltip`,
              styles.ScrollableChartTooltip,
            )}
            height={height}
            width={chartWidth}>
            {interaction.activeIndex > -1 &&
            interaction.x > -1 &&
            interaction.y > -1 ? (
              <Tooltip
                activeIndex={interaction.activeIndex}
                series={tooltipConfig.series}
                x={useConfig('x', tooltipConfig, interaction)}
                y={useConfig('y', interaction) - interaction.scrollTop}
              />
            ) : null}
          </svg>
        ) : null}
      </div>
    </div>
  );
};

ScrollableChart.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  configuration: PropTypes.shape({
    interaction: PropTypes.shape({
      direction: PropTypes.oneOf(
        Object.values(OrientationEnum).map(({id}) => id),
      ),
      height: PropTypes.number,
      tickRange: PropTypes.number,
      width: PropTypes.number,
      x: PropTypes.number,
      y: PropTypes.number,
    }),
    legend: PropTypes.shape({
      height: PropTypes.number,
      series: PropTypes.array,
    }),
    tooltip: PropTypes.shape({
      series: PropTypes.array,
      x: PropTypes.number,
      y: PropTypes.number,
    }),
  }),
  containerHeight: PropTypes.number,
  height: PropTypes.number,
  onInteraction: PropTypes.func,
  setWidth: PropTypes.func,
  showLegend: PropTypes.bool,
  showTooltip: PropTypes.bool,
};

ScrollableChart.defaultProps = {
  className: '',
  configuration: {
    interaction: {},
    legend: {},
    tooltip: {},
  },
  containerHeight: 0,
  height: 0,
  onInteraction: () => null,
  setWidth: () => null,
};
export default ScrollableChart;
